import { getLatestVersionContainingSupplier, VersionSnapshot } from './supplierDeadline';
import { BULK_DEADLINE_MAX_RECIPIENTS } from './bulkDeadlineResult';

// Sizing a bulk bid-deadline extension before it is sent.
//
// Mirrors resolveEligibleExtensions in src/helpers/supplierDeadline.js, which the route re-resolves
// from the database before writing anything. This exists so the user can see how far a send reaches
// before confirming it, and so the modal can refuse a selection the server would reject anyway —
// the server stays the authority.

export interface SelectionSupplier {
  supplierId: string;
  // Resolved by the caller against BidIntentStatus, so this module stays free of component imports.
  declined?: boolean;
}

export interface SelectionArea {
  areaId: string;
  suppliers?: SelectionSupplier[];
  versions?: VersionSnapshot[];
  // The area's version data could not be fetched. Its suppliers are unknown, not absent — counting
  // it as empty would quietly under-report the size of the send.
  unavailable?: boolean;
}

export interface ExtendableSelection {
  /** Suppliers that would be extended. */
  suppliers: number;
  /** Contact people that would be emailed — the number the cap is measured in. */
  contacts: number;
  /** Purchases holding at least one such supplier. */
  purchases: number;
  /** Selected purchases whose version data is missing, so they could not be counted at all. */
  unavailable: number;
  overLimit: boolean;
}

/**
 * What a bulk extension would reach across the selected purchases: suppliers that were sent a
 * version and have not declined, and the contact people they hold.
 *
 * `contacts` is what matters for the cap — each contact receives their own email, so a supplier
 * with four contacts costs four. A supplier on a version with no contact ids still counts as one:
 * the send may reach nobody, but a cap must never read zero for a real attempt.
 */
export function summarizeExtendableSelection(areas: SelectionArea[]): ExtendableSelection {
  let suppliers = 0;
  let contacts = 0;
  let purchases = 0;
  let unavailable = 0;

  areas.forEach((area) => {
    if (area.unavailable) {
      unavailable += 1;
      return;
    }

    let areaSuppliers = 0;
    const counted = new Set<string>();

    (area.suppliers ?? []).forEach((supplier) => {
      if (supplier.declined) return;

      const supplierId = String(supplier.supplierId);
      // A supplier listed twice on the same purchase is one supplier and one set of emails; the
      // route de-duplicates the same way.
      if (counted.has(supplierId)) return;

      const version = getLatestVersionContainingSupplier(supplierId, area.versions);
      if (!version) return;

      counted.add(supplierId);
      areaSuppliers += 1;
      const memberIds = new Set(
        (version.suppliers ?? [])
          .filter((s) => String(s.supplierId) === supplierId && s.supplierMemberId)
          .map((s) => String(s.supplierMemberId)),
      );
      contacts += Math.max(memberIds.size, 1);
    });

    if (areaSuppliers > 0) {
      suppliers += areaSuppliers;
      purchases += 1;
    }
  });

  return {
    suppliers,
    contacts,
    purchases,
    unavailable,
    overLimit: contacts > BULK_DEADLINE_MAX_RECIPIENTS,
  };
}
